import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';

const formatNumber = (n) => {
  if (n === null || n === undefined) return '—';
  try { return new Intl.NumberFormat('en-GB').format(n); } catch { return String(n); }
};

/**
 * Static Jobasti-styled KPI tile — no click-to-expand, no dropdown.
 * Shows: label · big number · delta vs previous period · small sparkline.
 *
 * Props:
 *   icon, label, value, subLabel, testId
 *   series: array of { bucket, value } for the sparkline
 *   bucketLabel: optional formatter for the previous-bucket label (e.g. "2026-01" → "Jan '26")
 *   size: 'sm' (default) or 'lg' — 'lg' renders a bigger, hero-style tile
 */
export const PolishedKpiTile = ({
  icon: Icon,
  label,
  value,
  subLabel,
  testId,
  series = [],
  bucketLabel = (k) => k,
  size = 'sm',
  onClick,
}) => {
  const sparklineData = series.map((p) => ({ x: p.bucket, y: p.value || 0 }));
  const lastVal = series.length > 0 ? series[series.length - 1].value || 0 : 0;
  const prevVal = series.length > 1 ? series[series.length - 2].value || 0 : 0;
  const prevBucket = series.length > 1 ? series[series.length - 2].bucket : null;
  let deltaPct = null;
  let deltaDir = 'flat';
  if (prevVal > 0) {
    deltaPct = Math.round(((lastVal - prevVal) / prevVal) * 100);
    deltaDir = deltaPct > 0 ? 'up' : (deltaPct < 0 ? 'down' : 'flat');
  } else if (lastVal > 0) {
    deltaPct = null;
    deltaDir = 'up';
  }

  const DeltaIcon = deltaDir === 'up' ? ArrowUpRight : deltaDir === 'down' ? ArrowDownRight : Minus;
  const deltaCls = deltaDir === 'up'
    ? 'text-emerald-700'
    : deltaDir === 'down' ? 'text-red-600' : 'text-gray-400';

  const isLarge = size === 'lg';
  const numberSize = isLarge ? 'text-[32px]' : 'text-[24px]';
  const labelSize = isLarge ? 'text-sm' : 'text-xs';
  const sparkW = isLarge ? 'w-24' : 'w-16';
  const sparkH = isLarge ? 'h-10' : 'h-8';
  const padding = isLarge ? 'px-6 py-5' : 'px-5 py-4';

  const clickable = typeof onClick === 'function';

  return (
    <div
      className={`bg-white border border-slate-200 rounded-2xl shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)] transition-all ${clickable ? 'cursor-pointer hover:border-blue-300 hover:shadow-[0_10px_34px_rgba(37,99,235,0.14)]' : ''}`}
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className={padding}>
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex-shrink-0">
            <Icon className="w-3.5 h-3.5 text-blue-600" />
          </span>
          <span className={`${labelSize} font-semibold text-gray-700 leading-snug flex-1`} data-testid={testId ? `${testId}-label` : undefined}>
            {label}
          </span>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className={`${numberSize} font-display font-semibold text-slate-900 tracking-tight leading-none tabular-nums truncate`} data-testid={testId ? `${testId}-value` : undefined}>
              {value}
            </div>
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mt-2">
              {deltaPct !== null && (
                <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${deltaCls}`} data-testid={testId ? `${testId}-delta` : undefined}>
                  <DeltaIcon className="w-3.5 h-3.5" />
                  {Math.abs(deltaPct)}%
                </span>
              )}
              {deltaPct !== null && prevBucket && (
                <span className="text-xs text-gray-500">vs {bucketLabel(prevBucket)}</span>
              )}
              {subLabel && <span className="text-xs text-gray-500 truncate">{subLabel}</span>}
              {deltaPct === null && !subLabel && series.length > 1 && (
                <span className="text-xs text-gray-400">no prior data</span>
              )}
            </div>
          </div>

          {sparklineData.length > 1 && (
            <div className={`${sparkH} ${sparkW} flex-shrink-0`} data-testid={testId ? `${testId}-sparkline` : undefined}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparklineData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`sg-${testId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="y" stroke="#3b82f6" strokeWidth={1.5} fill={`url(#sg-${testId})`} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Granularity segmented control — Month / Quarter / Year (compact, matches Jobasti blue).
 */
export const GranularityTabs = ({ value, onChange, testId = 'granularity-tabs' }) => {
  const options = [
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' },
  ];
  return (
    <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200" data-testid={testId} role="tablist">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            value === o.key ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'text-gray-500 hover:text-gray-800'
          }`}
          data-testid={`${testId}-${o.key}`}
          role="tab"
          aria-selected={value === o.key}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

/**
 * Section header — clear title + optional subtitle above a group of tiles / charts.
 */
export const SectionEyebrow = ({ title, subtitle, testId }) => (
  <div className="mb-4" data-testid={testId}>
    <h2 className="text-base font-display font-semibold text-slate-900 tracking-tight">{title}</h2>
    {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
  </div>
);

/** Human-friendly label for a bucket key. */
export const bucketLabelFor = (granularity) => (key) => {
  if (!key) return '';
  if (granularity === 'month') {
    const [y, m] = key.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  }
  if (granularity === 'quarter') return key; // 2026-Q1
  return key; // year
};
